import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { DollarSign, TrendingUp, TrendingDown, Receipt, CreditCard, PieChart, BarChart3, FileText, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

export const FinancePage = () => {
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('monthly');
  const [summary, setSummary] = useState(null);
  const [balanceSheet, setBalanceSheet] = useState(null);
  const [profitLoss, setProfitLoss] = useState(null);
  const [trialBalance, setTrialBalance] = useState(null);
  const [agingReport, setAgingReport] = useState(null);

  useEffect(() => {
    loadData();
  }, [period]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [summaryRes, balanceRes, plRes, trialRes, agingRes] = await Promise.all([
        api.get(`/finance/summary?period=${period}`),
        api.get('/finance/balance-sheet'),
        api.get(`/finance/profit-loss?period=${period}`),
        api.get('/finance/trial-balance'),
        api.get('/reports/aging')
      ]);
      setSummary(summaryRes.data);
      setBalanceSheet(balanceRes.data);
      setProfitLoss(plRes.data);
      setTrialBalance(trialRes.data);
      setAgingReport(agingRes.data);
    } catch (error) {
      toast.error('Failed to load financial data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return `SAR ${(amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Loading financial data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="finance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Finance</h1>
          <p className="text-muted-foreground mt-1">Financial reports and analysis</p>
        </div>
        <div className="flex gap-4">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="monthly">Monthly</SelectItem>
              <SelectItem value="quarterly">Quarterly</SelectItem>
              <SelectItem value="yearly">Yearly</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={loadData}>Refresh</Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Invoiced</p>
                <p className="text-2xl font-bold">{formatCurrency(summary?.revenue?.total_invoiced)}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-lg">
                <Receipt className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Receivables</p>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(summary?.revenue?.total_receivables)}</p>
              </div>
              <div className="p-3 bg-amber-100 rounded-lg">
                <ArrowDownRight className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Payables</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(summary?.expenses?.total_payables)}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-lg">
                <ArrowUpRight className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Net Profit</p>
                <p className={`text-2xl font-bold ${(summary?.profit_loss?.net_profit || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatCurrency(summary?.profit_loss?.net_profit)}
                </p>
              </div>
              <div className={`p-3 rounded-lg ${(summary?.profit_loss?.net_profit || 0) >= 0 ? 'bg-green-100' : 'bg-red-100'}`}>
                {(summary?.profit_loss?.net_profit || 0) >= 0 ? (
                  <TrendingUp className="h-6 w-6 text-green-600" />
                ) : (
                  <TrendingDown className="h-6 w-6 text-red-600" />
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* VAT Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">VAT Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-muted-foreground">VAT Collected</p>
              <p className="text-xl font-bold text-blue-600">{formatCurrency(summary?.vat?.collected)}</p>
            </div>
            <div className="p-4 bg-red-50 rounded-lg">
              <p className="text-sm text-muted-foreground">VAT Paid</p>
              <p className="text-xl font-bold text-red-600">{formatCurrency(summary?.vat?.paid)}</p>
            </div>
            <div className={`p-4 rounded-lg ${(summary?.vat?.net || 0) >= 0 ? 'bg-green-50' : 'bg-amber-50'}`}>
              <p className="text-sm text-muted-foreground">Net VAT Payable</p>
              <p className={`text-xl font-bold ${(summary?.vat?.net || 0) >= 0 ? 'text-green-600' : 'text-amber-600'}`}>
                {formatCurrency(summary?.vat?.net)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for detailed reports */}
      <Tabs defaultValue="pnl" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pnl">Profit & Loss</TabsTrigger>
          <TabsTrigger value="balance">Balance Sheet</TabsTrigger>
          <TabsTrigger value="trial">Trial Balance</TabsTrigger>
          <TabsTrigger value="aging">Aging Report</TabsTrigger>
        </TabsList>

        {/* Profit & Loss */}
        <TabsContent value="pnl">
          <Card>
            <CardHeader>
              <CardTitle>Profit & Loss Statement</CardTitle>
              <p className="text-sm text-muted-foreground">Period: {period}</p>
            </CardHeader>
            <CardContent>
              {profitLoss && (
                <div className="space-y-6">
                  {/* Revenue */}
                  <div>
                    <h4 className="font-semibold text-green-700 mb-2">REVENUE</h4>
                    <div className="space-y-2 pl-4">
                      <div className="flex justify-between">
                        <span>Gross Revenue</span>
                        <span className="font-medium">{formatCurrency(profitLoss.revenue?.gross_revenue)}</span>
                      </div>
                      <div className="flex justify-between text-sm text-muted-foreground">
                        <span>VAT Collected</span>
                        <span>{formatCurrency(profitLoss.revenue?.vat_collected)}</span>
                      </div>
                      <div className="flex justify-between font-semibold border-t pt-2">
                        <span>Total Revenue</span>
                        <span>{formatCurrency(profitLoss.revenue?.total_revenue)}</span>
                      </div>
                    </div>
                  </div>

                  {/* COGS */}
                  <div>
                    <h4 className="font-semibold text-red-700 mb-2">COST OF GOODS SOLD</h4>
                    <div className="space-y-2 pl-4">
                      <div className="flex justify-between">
                        <span>Supplier Costs</span>
                        <span className="font-medium">{formatCurrency(profitLoss.cost_of_goods_sold?.supplier_costs)}</span>
                      </div>
                      <div className="flex justify-between font-semibold border-t pt-2">
                        <span>Gross Profit</span>
                        <span className="text-green-600">{formatCurrency(profitLoss.cost_of_goods_sold?.gross_profit)}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span>Gross Margin</span>
                        <Badge variant="outline">{profitLoss.cost_of_goods_sold?.gross_margin_percent?.toFixed(1)}%</Badge>
                      </div>
                    </div>
                  </div>

                  {/* Operating Expenses */}
                  <div>
                    <h4 className="font-semibold text-orange-700 mb-2">OPERATING EXPENSES</h4>
                    <div className="space-y-2 pl-4">
                      <div className="flex justify-between">
                        <span>Administrative</span>
                        <span>{formatCurrency(profitLoss.operating_expenses?.administrative)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Marketing</span>
                        <span>{formatCurrency(profitLoss.operating_expenses?.marketing)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Depreciation</span>
                        <span>{formatCurrency(profitLoss.operating_expenses?.depreciation)}</span>
                      </div>
                      <div className="flex justify-between font-semibold border-t pt-2">
                        <span>Operating Income</span>
                        <span>{formatCurrency(profitLoss.operating_income)}</span>
                      </div>
                    </div>
                  </div>

                  {/* Net Income */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="font-bold text-lg">NET INCOME</span>
                      <span className={`text-2xl font-bold ${profitLoss.net_income >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {formatCurrency(profitLoss.net_income)}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm mt-2">
                      <span>Net Margin</span>
                      <Badge variant={profitLoss.net_margin_percent >= 0 ? 'default' : 'destructive'}>
                        {profitLoss.net_margin_percent?.toFixed(1)}%
                      </Badge>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Balance Sheet */}
        <TabsContent value="balance">
          <Card>
            <CardHeader>
              <CardTitle>Balance Sheet</CardTitle>
              <p className="text-sm text-muted-foreground">As of: {new Date(balanceSheet?.as_of_date || Date.now()).toLocaleDateString()}</p>
            </CardHeader>
            <CardContent>
              {balanceSheet && (
                <div className="grid grid-cols-2 gap-8">
                  {/* Assets */}
                  <div>
                    <h4 className="font-bold text-lg mb-4 text-blue-700">ASSETS</h4>
                    
                    <div className="space-y-4">
                      <div>
                        <h5 className="font-semibold text-sm text-muted-foreground mb-2">Current Assets</h5>
                        <div className="space-y-2 pl-4">
                          <div className="flex justify-between">
                            <span>Cash</span>
                            <span>{formatCurrency(balanceSheet.assets?.current_assets?.cash)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Accounts Receivable</span>
                            <span>{formatCurrency(balanceSheet.assets?.current_assets?.accounts_receivable)}</span>
                          </div>
                          <div className="flex justify-between font-medium border-t pt-1">
                            <span>Total Current</span>
                            <span>{formatCurrency(balanceSheet.assets?.current_assets?.total_current)}</span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h5 className="font-semibold text-sm text-muted-foreground mb-2">Fixed Assets</h5>
                        <div className="space-y-2 pl-4">
                          <div className="flex justify-between">
                            <span>Fleet Value</span>
                            <span>{formatCurrency(balanceSheet.assets?.fixed_assets?.fleet_value)}</span>
                          </div>
                          <div className="flex justify-between text-red-600">
                            <span>Less: Depreciation</span>
                            <span>({formatCurrency(balanceSheet.assets?.fixed_assets?.accumulated_depreciation)})</span>
                          </div>
                          <div className="flex justify-between font-medium border-t pt-1">
                            <span>Net Fixed Assets</span>
                            <span>{formatCurrency(balanceSheet.assets?.fixed_assets?.net_fleet_value)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-blue-50 p-3 rounded-lg">
                        <div className="flex justify-between font-bold">
                          <span>TOTAL ASSETS</span>
                          <span>{formatCurrency(balanceSheet.assets?.total_assets)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Liabilities & Equity */}
                  <div>
                    <h4 className="font-bold text-lg mb-4 text-red-700">LIABILITIES & EQUITY</h4>
                    
                    <div className="space-y-4">
                      <div>
                        <h5 className="font-semibold text-sm text-muted-foreground mb-2">Current Liabilities</h5>
                        <div className="space-y-2 pl-4">
                          <div className="flex justify-between">
                            <span>Accounts Payable</span>
                            <span>{formatCurrency(balanceSheet.liabilities?.current_liabilities?.accounts_payable)}</span>
                          </div>
                        </div>
                      </div>

                      <div>
                        <h5 className="font-semibold text-sm text-muted-foreground mb-2">Long-term Liabilities</h5>
                        <div className="space-y-2 pl-4">
                          <div className="flex justify-between">
                            <span>Loans Payable</span>
                            <span>{formatCurrency(balanceSheet.liabilities?.long_term_liabilities?.loans_payable)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-red-50 p-3 rounded-lg">
                        <div className="flex justify-between font-bold">
                          <span>Total Liabilities</span>
                          <span>{formatCurrency(balanceSheet.liabilities?.total_liabilities)}</span>
                        </div>
                      </div>

                      <div>
                        <h5 className="font-semibold text-sm text-muted-foreground mb-2">Equity</h5>
                        <div className="space-y-2 pl-4">
                          <div className="flex justify-between">
                            <span>Owner&apos;s Equity</span>
                            <span>{formatCurrency(balanceSheet.equity?.owners_equity)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>Retained Earnings</span>
                            <span>{formatCurrency(balanceSheet.equity?.retained_earnings)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="bg-green-50 p-3 rounded-lg">
                        <div className="flex justify-between font-bold">
                          <span>TOTAL L + E</span>
                          <span>{formatCurrency(balanceSheet.liabilities?.total_liabilities + balanceSheet.equity?.total_equity)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {balanceSheet?.balance_check?.is_balanced && (
                <div className="mt-4 p-2 bg-green-100 text-green-800 text-center rounded text-sm">
                  ✓ Balance Sheet is balanced
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Trial Balance */}
        <TabsContent value="trial">
          <Card>
            <CardHeader>
              <CardTitle>Trial Balance</CardTitle>
            </CardHeader>
            <CardContent>
              {trialBalance && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-3">Account</th>
                        <th className="text-right py-3">Debit</th>
                        <th className="text-right py-3">Credit</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="py-2">Fixed Assets</td>
                        <td className="text-right">{formatCurrency(trialBalance.debit_accounts?.fixed_assets)}</td>
                        <td className="text-right">-</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2">Accounts Receivable</td>
                        <td className="text-right">{formatCurrency(trialBalance.debit_accounts?.accounts_receivable)}</td>
                        <td className="text-right">-</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2">Cash</td>
                        <td className="text-right">{formatCurrency(trialBalance.debit_accounts?.cash)}</td>
                        <td className="text-right">-</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2">Accounts Payable</td>
                        <td className="text-right">-</td>
                        <td className="text-right">{formatCurrency(trialBalance.credit_accounts?.accounts_payable)}</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2">Revenue</td>
                        <td className="text-right">-</td>
                        <td className="text-right">{formatCurrency(trialBalance.credit_accounts?.revenue)}</td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2">Retained Earnings</td>
                        <td className="text-right">-</td>
                        <td className="text-right">{formatCurrency(trialBalance.credit_accounts?.retained_earnings)}</td>
                      </tr>
                      <tr className="font-bold bg-gray-50">
                        <td className="py-3">TOTALS</td>
                        <td className="text-right">{formatCurrency(trialBalance.balance_check?.total_debits)}</td>
                        <td className="text-right">{formatCurrency(trialBalance.balance_check?.total_credits)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Aging Report */}
        <TabsContent value="aging">
          <Card>
            <CardHeader>
              <CardTitle>Accounts Receivable Aging</CardTitle>
            </CardHeader>
            <CardContent>
              {agingReport && (
                <div className="grid grid-cols-4 gap-4">
                  <div className="p-4 bg-green-50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">0-30 Days</p>
                    <p className="text-2xl font-bold text-green-600">{formatCurrency(agingReport.aging?.["0-30"]?.total)}</p>
                    <p className="text-xs text-muted-foreground">{agingReport.aging?.["0-30"]?.count} invoices</p>
                  </div>
                  <div className="p-4 bg-yellow-50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">31-60 Days</p>
                    <p className="text-2xl font-bold text-yellow-600">{formatCurrency(agingReport.aging?.["31-60"]?.total)}</p>
                    <p className="text-xs text-muted-foreground">{agingReport.aging?.["31-60"]?.count} invoices</p>
                  </div>
                  <div className="p-4 bg-orange-50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">61-90 Days</p>
                    <p className="text-2xl font-bold text-orange-600">{formatCurrency(agingReport.aging?.["61-90"]?.total)}</p>
                    <p className="text-xs text-muted-foreground">{agingReport.aging?.["61-90"]?.count} invoices</p>
                  </div>
                  <div className="p-4 bg-red-50 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground">90+ Days</p>
                    <p className="text-2xl font-bold text-red-600">{formatCurrency(agingReport.aging?.["90+"]?.total)}</p>
                    <p className="text-xs text-muted-foreground">{agingReport.aging?.["90+"]?.count} invoices</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
